import React, { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Search,
  X,
  Trash2,
  Loader2,
  Mail,
  Phone,
  Building2,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Reusable list/CRUD component for an entity (Contacts / Companies / etc.).
 *
 * Props:
 *  - endpoint:    e.g. "/crm/contacts"
 *  - title:       page heading (singular/plural pair handled outside)
 *  - fields:      [{key, label, type: 'text'|'email'|'phone'|'textarea'|'select',
 *                   placeholder?, required?, options?, showInList?}]
 *  - primaryKey:  field used for the bold title in the list (default 'name')
 *  - secondaryKeys: array of field keys shown smaller under the title
 *  - emptyHelp:   short string shown when there are 0 records
 *  - newLabel:    button label, default 'New record'
 *  - testIdPrefix: prefix used for data-testid attributes
 */
export default function RecordList({
  endpoint,
  fields,
  primaryKey = "name",
  secondaryKeys = ["email", "phone"],
  emptyHelp = "No records yet.",
  newLabel = "New record",
  testIdPrefix = "record",
}) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // record being edited or null for new

  const emptyForm = useMemo(() => {
    const f = {};
    fields.forEach((fd) => {
      f[fd.key] = "";
    });
    return f;
  }, [fields]);

  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setError("");
    try {
      const { data } = await api.get(endpoint, {
        params: query ? { q: query } : {},
      });
      setItems(data);
    } catch (e) {
      setError(apiError(e));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (rec) => {
    setEditing(rec);
    const f = { ...emptyForm };
    fields.forEach((fd) => {
      f[fd.key] = rec[fd.key] ?? "";
    });
    setForm(f);
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
    setEditing(null);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // strip empty optional strings to null
      const payload = { ...form };
      fields.forEach((fd) => {
        if (!fd.required && payload[fd.key] === "") payload[fd.key] = null;
      });
      if (editing) {
        await api.patch(`${endpoint}/${editing.id}`, payload);
        toast.success("Saved.");
      } else {
        await api.post(endpoint, payload);
        toast.success("Created.");
      }
      closeDialog();
      load();
    } catch (e2) {
      toast.error(apiError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async () => {
    if (!editing) return;
    if (!window.confirm("Delete this record? This can't be undone.")) return;
    setSubmitting(true);
    try {
      await api.delete(`${endpoint}/${editing.id}`);
      toast.success("Deleted.");
      closeDialog();
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Toolbar */}
      <div className="border-b border-[#E8E6E1] px-6 md:px-10 py-4 bg-[#FDFCFB] sticky top-14 md:top-0 z-10 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6A68]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            data-testid={`${testIdPrefix}-search`}
            className="editorial-input pl-6 !py-2 !text-sm"
          />
        </div>
        <button
          type="button"
          onClick={openCreate}
          data-testid={`${testIdPrefix}-new-button`}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1A1A1A] text-[#FDFCFB] text-sm hover:bg-[#333] transition-colors"
        >
          <Plus className="w-4 h-4" />
          {newLabel}
        </button>
      </div>

      <div className="px-6 md:px-10 py-6 md:py-8">
        {error && <div className="mb-4 text-sm text-[#B03A2E]">{error}</div>}

        {items === null ? (
          <div className="flex items-center gap-2 text-sm text-[#6B6A68]">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 px-6 rounded-2xl bg-[#F5F4F0]">
            <div className="caption mb-3">Empty</div>
            <p className="font-serif-display text-2xl md:text-3xl text-[#1A1A1A] tracking-tight">
              {emptyHelp}
            </p>
            <button
              type="button"
              onClick={openCreate}
              data-testid={`${testIdPrefix}-empty-cta`}
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1A1A1A] text-[#FDFCFB] text-sm"
            >
              <Plus className="w-4 h-4" /> {newLabel}
            </button>
          </div>
        ) : (
          <ul
            data-testid={`${testIdPrefix}-list`}
            className="divide-y divide-[#E8E6E1] border border-[#E8E6E1] rounded-2xl bg-[#FDFCFB] overflow-hidden"
          >
            {items.map((rec) => (
              <li key={rec.id}>
                <button
                  type="button"
                  onClick={() => openEdit(rec)}
                  data-testid={`${testIdPrefix}-row-${rec.id}`}
                  className="w-full text-left px-5 py-4 hover:bg-[#F5F4F0] transition-colors flex items-center gap-4"
                >
                  <div className="w-9 h-9 rounded-full bg-[#F5F4F0] flex items-center justify-center font-serif-display text-sm text-[#1A1A1A] uppercase shrink-0">
                    {(rec[primaryKey] || "?").slice(0, 1)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[15px] text-[#1A1A1A] font-medium tracking-tight truncate">
                      {rec[primaryKey] || "—"}
                    </div>
                    <div className="text-xs text-[#6B6A68] flex flex-wrap items-center gap-x-4 gap-y-0.5 mt-0.5">
                      {secondaryKeys.map((k) => {
                        if (!rec[k]) return null;
                        const Icon =
                          k === "email" ? Mail : k === "phone" ? Phone : k === "domain" ? Building2 : null;
                        return (
                          <span key={k} className="inline-flex items-center gap-1">
                            {Icon && <Icon className="w-3 h-3" />} {rec[k]}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  <div className="font-mono-tech text-[10px] tracking-[0.16em] uppercase text-[#6B6A68] hidden sm:block">
                    Edit →
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Dialog */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div
            className="absolute inset-0 bg-[#1A1A1A]/40"
            onClick={submitting ? undefined : closeDialog}
          />
          <form
            onSubmit={onSubmit}
            data-testid={`${testIdPrefix}-form`}
            className="relative w-full sm:max-w-lg bg-[#FDFCFB] rounded-t-2xl sm:rounded-2xl border border-[#E8E6E1] shadow-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="px-6 pt-6 pb-4 border-b border-[#E8E6E1] flex items-center justify-between">
              <div>
                <div className="caption mb-1">
                  {editing ? "Edit record" : "New record"}
                </div>
                <h3 className="font-serif-display text-2xl tracking-tight text-[#1A1A1A]">
                  {editing ? editing[primaryKey] || "Untitled" : newLabel}
                </h3>
              </div>
              <button
                type="button"
                onClick={closeDialog}
                disabled={submitting}
                className="p-1.5 rounded-full hover:bg-[#F5F4F0]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-1.5">
              {fields.map((fd) => (
                <div key={fd.key}>
                  <label className="caption block mb-1">
                    {fd.label}
                    {fd.required && " *"}
                  </label>
                  {fd.type === "textarea" ? (
                    <textarea
                      className="editorial-input"
                      rows={3}
                      value={form[fd.key]}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, [fd.key]: e.target.value }))
                      }
                      placeholder={fd.placeholder}
                      required={fd.required}
                      disabled={submitting}
                      data-testid={`${testIdPrefix}-field-${fd.key}`}
                    />
                  ) : fd.type === "select" ? (
                    <select
                      className="editorial-input"
                      value={form[fd.key]}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, [fd.key]: e.target.value }))
                      }
                      required={fd.required}
                      disabled={submitting}
                      data-testid={`${testIdPrefix}-field-${fd.key}`}
                    >
                      <option value="">{fd.placeholder || "Select…"}</option>
                      {fd.options?.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={fd.type || "text"}
                      className="editorial-input"
                      value={form[fd.key]}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, [fd.key]: e.target.value }))
                      }
                      placeholder={fd.placeholder}
                      required={fd.required}
                      disabled={submitting}
                      data-testid={`${testIdPrefix}-field-${fd.key}`}
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="px-6 py-4 border-t border-[#E8E6E1] flex items-center justify-between gap-3 bg-[#FDFCFB] sticky bottom-0">
              <div>
                {editing && (
                  <button
                    type="button"
                    onClick={onDelete}
                    disabled={submitting}
                    data-testid={`${testIdPrefix}-delete`}
                    className="inline-flex items-center gap-1.5 text-sm text-[#B03A2E] hover:underline underline-offset-4"
                  >
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={closeDialog}
                  disabled={submitting}
                  className="px-4 py-2.5 text-sm rounded-full hover:bg-[#F5F4F0]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid={`${testIdPrefix}-submit`}
                  className="px-5 py-2.5 rounded-full bg-[#1A1A1A] text-[#FDFCFB] text-sm hover:bg-[#333] disabled:opacity-60 inline-flex items-center gap-2"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editing ? "Save changes" : "Create"}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
